// services/ticketService.js
import { API } from "../../apis/api";
import {
  mapStatusForAPI,
  convertStringToBoolean,
  formatCaseTypeForAPI,
  formatCaseTypeFromAPI,
  mapStatusFromAPI,
} from "../../utils/utils";
import { decryptDocumentUrl } from "../../utils/cryptoUtils";

export const prepareDividendEntriesFromTicket = (dividend_entries = []) =>
{
  const arr = dividend_entries.map((entry) => ({
    financialYear: entry.financial_year || "",
    paymentDate: entry.payment_date ? entry.payment_date.split("T")[0] : "",
    warrantNo: entry.warrant_no || "",
    amount: entry.dividend_amount || "",
  }));
  // Ensure at least one entry exists
  if (arr.length === 0) {
    return [
      {
        financialYear: "",
        paymentDate: "",
        warrantNo: "",
        amount: "",
      },
    ];
  }
  // Ensure all entries have the same keys
  return arr.map((entry) => ({
    financialYear: entry.financialYear || "",
    paymentDate: entry.paymentDate || "",
    warrantNo: entry.warrantNo || "",
    amount: entry.amount || "",
  }));
}

export const prepareTicketData = (formData, dividendEntries, isUpdate = false, userId) => {
  const apiData = {
    company_name: formData.companyName || "",
    shareholder_name: formData.shareholderName || "",
    claimant_name: formData.claimantName || "",
    beneficiary_account_no: formData.beneficiaryAccount || "",
    type_of_security: formData.securityType || "",
    numbers_of_shares: formData.numberOfShares || "",
    face_value: formData.shareFaceValue || "",
    folio_number: formData.folioId || "",
    demat_id: formData.dematId || "",
    srn: formData.SRN || "",
    iepf_transfer_date: formData.transferDate
      ? new Date(formData.transferDate).toISOString()
      : "",
    unclaimed_suspense_account:
      convertStringToBoolean(formData.unclaimedSuspenseAccount) ,
    case_covered_iepf_rules_7: convertStringToBoolean(formData.iepfRules) ,
    claimable_amount: formData.claimableAmount ,
    signature_mismatch: convertStringToBoolean(formData.signatureMismatch),
    mismatch_in_nature_of_security:
      convertStringToBoolean(formData.securityMismatch) ,
    any_litigation_pending:
      convertStringToBoolean(formData.litigationPending) ,
    dividend_info: dividendEntries
      .filter(
        (entry) =>
          entry.financialYear ||
          entry.paymentDate ||
          entry.warrantNo ||
          entry.amount
      )
      .map((entry) => ({
        financial_year: entry.financialYear || "",
        payment_date: entry.paymentDate
          ? new Date(entry.paymentDate).toISOString()
          : "",
        warrant_no: entry.warrantNo || "",
        dividend_amount: entry.amount || "",
      })),
    remarks: formData.remark
      ? [{ remark: formData.remark, is_active: true }]
      : [],
    sub:userId,
  };

  if (!isUpdate) {
    apiData.status = mapStatusForAPI(formData.status)
    apiData.case_type = formatCaseTypeForAPI(formData.caseType)
  }

  return apiData;
};

export const prepareFormDataFromTicket = (apiData) => ({
  companyName: apiData.company_name || "",
  shareholderName: apiData.shareholder_name || "",
  claimantName: apiData.claimant_name || "",
  beneficiaryAccount: apiData.beneficiary_account_no || "",
  securityType: apiData.type_of_security || "",
  numberOfShares: apiData.numbers_of_shares.toString() || "",
  shareFaceValue: apiData.face_value || "",
  folioId: apiData.folio_number || "",
  dematId: apiData.demat_id || "",
  SRN: apiData.srn || "",
  transferDate: apiData.iepf_transfer_date
    ? apiData.iepf_transfer_date.split("T")[0]
    : "",
  claimableAmount: apiData.claimable_amount || "",
  unclaimedSuspenseAccount: apiData.unclaimed_suspense_account
    ? "Yes"
    : "No" || false,
  iepfRules: apiData.case_covered_iepf_rules_7 ? "Yes" : "No",
  signatureMismatch: apiData.signature_mismatch ? "Yes" : "No",
  securityMismatch: apiData.security_mismatch ? "Yes" : "No",
  litigationPending: apiData.any_litigation_pending ? "Yes" : "No",

  status: mapStatusFromAPI(apiData.status),
  caseType: formatCaseTypeFromAPI(apiData.case_type || []),
  remark:
    Array.isArray(apiData.remarks) && apiData.remarks.length > 0
      ? apiData.remarks[0].remark
      : "",
});



export const createTicket = async (formData, dividendEntries,userId) => {
  console.log("Creating ticket with formData:", formData);
  const ticketData = prepareTicketData(formData, dividendEntries,false,userId);
  const response = await API.post("/ticket/create-ticket/", ticketData);
  return response.data;
};

export const triggerEmail = async (ticketId,userId) => {
  const response = await API.post("/email/email-trigger/", {
    ticket_id: ticketId,
    sub:userId,
  });
  return response.data;
};

export const fetchTicketById = async (ticketCode,userId) => {
  try {
    const response = await API.post("/ticket/get-ticket-details/", {
      ticket_code: ticketCode,
      sub:userId,
    });
    return response.data.data;
  } catch (error) {
    console.error("Error fetching ticket:", error);
    throw error;
  }
};

export const updateTicket = async (ticketCode, formData, dividendEntries,userId) => {
  console.log("formData", formData);
  const ticketData = prepareTicketData(formData, dividendEntries, true,userId);
  const response = await API.post("/ticket/update-ticket-details/", {
    ticket_code: ticketCode,
    ...ticketData,
  });
  return response;
};

export const updateTicketSRN = async (ticketId, srn,userId) => {
  const response = await API.post("/ticket/update-ticket-status/", {
    ticket_id: ticketId,
    srn,
    sub:userId,
    ticket_status:"SRN_UPDATE"
  });
  return response;
};

export const fetchTicketDocuments = async (ticketCode,userId) => {
  try {
    const response = await API.post("/document/get-ticket-document-list/", {
      ticket_code: ticketCode,
      sub:userId,
    });
    return response.data.data;
  } catch (error) {
    console.error("Error fetching ticket documents:", error);
    throw error;
  }
};

export const getDocumentDownloadUrl = async (ticketCode, documentType,userId) => {
  try {
    const response = await API.post("/document/get-download-url/", {
      ticket_code: ticketCode,
      sub:userId,
      document_type: documentType,
    });
    
    // Decrypt the encrypted URL'

    
    const encryptedUrl = response.data.download_url;
    const decryptedUrl = decryptDocumentUrl(encryptedUrl);
    
    return decryptedUrl;
  } catch (error) {
    console.error("Error fetching document download URL:", error);
    throw error;
  }
};


export const retriggerAIAgent = async (ticketId, documentTypes,userId) => {
  try {
    const response = await API.post(`/api/tickets/${ticketId}/retrigger-ai`, {
      ticket_id: ticketId,
      document_types: documentTypes,
      sub:userId,
    });
    return response.data;
  } catch (error) {
    console.error('Error retriggering AI agent:', error);
    throw error;
  }
};

export const deleteTicket = async (ticketCode,userId) => {
  try {
    const response = await API.post("/ticket/delete-ticket/", {
      ticket_code: ticketCode,
      sub:userId,
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting ticket:", error);
    throw error;
  }
};
